import React, { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Copy, Check, ChevronRight, ChevronDown, Database, Layers, GitBranch, Globe, Code2, FileText, Shield } from "lucide-react";

const getTreeStructure = (objects, entityName) => {
    const name = (entityName || "Entity").toUpperCase();
    return [
        {
            id: "tables",
            label: "Database Tables",
            icon: <Database size={14} />,
            color: "text-amber-400",
            children: [
                objects.ddl_table && {
                    id: "ddl_table",
                    label: `Z${name}`,
                    icon: <Database size={13} />,
                    color: "text-amber-300",
                    lang: "sql",
                    code: objects.ddl_table,
                },
                objects.ddl_draft && {
                    id: "ddl_draft",
                    label: `Z${name}_D`,
                    icon: <Database size={13} />,
                    color: "text-amber-300",
                    lang: "sql",
                    code: objects.ddl_draft,
                },
            ].filter(Boolean),
        },
        {
            id: "cds",
            label: "CDS Views",
            icon: <Layers size={14} />,
            color: "text-blue-400",
            children: [
                objects.zi_view && {
                    id: "zi_view",
                    label: `ZI_${name}`,
                    icon: <Layers size={13} />,
                    color: "text-blue-300",
                    lang: "sql",
                    code: objects.zi_view,
                },
                objects.zr_view && {
                    id: "zr_view",
                    label: `ZR_${name}`,
                    icon: <Layers size={13} />,
                    color: "text-blue-400",
                    lang: "sql",
                    code: objects.zr_view,
                },
                objects.zc_view && {
                    id: "zc_view",
                    label: `ZC_${name}`,
                    icon: <Layers size={13} />,
                    color: "text-violet-300",
                    lang: "sql",
                    code: objects.zc_view,
                },
                objects.metadata_ext && {
                    id: "metadata_ext",
                    label: `ZC_${name}.ddlx`,
                    icon: <FileText size={13} />,
                    color: "text-violet-300",
                    lang: "sql",
                    code: objects.metadata_ext,
                },
            ].filter(Boolean),
        },
        {
            id: "behavior",
            label: "Behavior Objects",
            icon: <GitBranch size={14} />,
            color: "text-emerald-400",
            children: [
                objects.behavior_def_root && {
                    id: "behavior_def_root",
                    label: `ZR_${name}.bdef`,
                    icon: <FileText size={13} />,
                    color: "text-emerald-300",
                    lang: "abap",
                    code: objects.behavior_def_root,
                },
                objects.behavior_impl && {
                    id: "behavior_impl",
                    label: `ZBP_R_${name}.clas`,
                    icon: <Code2 size={13} />,
                    color: "text-emerald-300",
                    lang: "abap",
                    code: objects.behavior_impl,
                },
                objects.behavior_def_projection && {
                    id: "behavior_def_projection",
                    label: `ZC_${name}.bdef`,
                    icon: <FileText size={13} />,
                    color: "text-emerald-200",
                    lang: "abap",
                    code: objects.behavior_def_projection,
                },
            ].filter(Boolean),
        },
        {
            id: "access",
            label: "Access Control",
            icon: <Shield size={14} />,
            color: "text-red-400",
            children: [
                objects.access_control_zi && {
                    id: "access_control_zi",
                    label: `ZI_${name}.dcl`,
                    icon: <Shield size={13} />,
                    color: "text-red-300",
                    lang: "sql",
                    code: objects.access_control_zi,
                },
                objects.access_control_zc && {
                    id: "access_control_zc",
                    label: `ZC_${name}.dcl`,
                    icon: <Shield size={13} />,
                    color: "text-red-300",
                    lang: "sql",
                    code: objects.access_control_zc,
                },
            ].filter(Boolean),
        },
        {
            id: "service",
            label: "Service Objects",
            icon: <Globe size={14} />,
            color: "text-cyan-400",
            children: [
                objects.service_def && {
                    id: "service_def",
                    label: `ZSD_${name}.srvd`,
                    icon: <FileText size={13} />,
                    color: "text-cyan-300",
                    lang: "sql",
                    code: objects.service_def,
                },
                objects.service_binding && {
                    id: "service_binding",
                    label: `ZSB_${name}_V4.srvb`,
                    icon: <Globe size={13} />,
                    color: "text-cyan-300",
                    lang: "text",
                    code: objects.service_binding,
                },
            ].filter(Boolean),
        },
    ].filter(n => n.children.length > 0);
};

const TreeNode = ({ node, selectedId, onSelect, depth = 0 }) => {
    const [open, setOpen] = useState(true);
    const isFolder = !!node.children;
    const isSelected = selectedId === node.id;

    return (
        <div>
            <div
                onClick={() => {
                    if (isFolder) setOpen(!open);
                    else onSelect(node);
                }}
                className={`flex items-center gap-2 py-1.5 cursor-pointer rounded-lg transition-all text-left
          ${isSelected ? "bg-blue-600/30 border border-blue-500/40" : "hover:bg-white/5"}
        `}
                style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: "8px" }}
            >
                {isFolder && (
                    <span className="text-white/30 shrink-0">
                        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </span>
                )}
                {!isFolder && <span className="w-3 shrink-0" />}
                <span className={`shrink-0 ${node.color}`}>{node.icon}</span>
                <span className={`text-[11px] font-bold truncate ${isSelected ? "text-white" : "text-white/60"}`}>
                    {node.label}
                </span>
            </div>
            {isFolder && open && (
                <div>
                    {node.children.map(child => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const RapExplorer = ({ rapObjects }) => {
    const [selectedNode, setSelectedNode] = useState(null);
    const [copied, setCopied] = useState(false);

    if (!rapObjects?.objects) return null;

    const tree = getTreeStructure(rapObjects.objects, rapObjects.entityName);
    const totalObjects = Object.values(rapObjects.objects).filter(Boolean).length;

    const handleCopy = () => {
        if (selectedNode?.code) {
            navigator.clipboard.writeText(selectedNode.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleCopyAll = () => {
        const all = Object.values(rapObjects.objects)
            .filter(Boolean)
            .join("\n\n" + "─".repeat(60) + "\n\n");
        navigator.clipboard.writeText(all);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="mt-6 rounded-[16px] overflow-hidden border border-white/10 shadow-2xl animate-in slide-in-from-bottom duration-700">
            {/* Header */}
            <div className="bg-white/5 px-6 py-3 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Code2 size={16} className="text-blue-400" />
                    <span className="text-[11px] font-black text-white uppercase tracking-widest">
                        RAP Stack Explorer
                    </span>
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 uppercase tracking-widest">
                        {rapObjects.entityName}
                    </span>
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 uppercase tracking-widest">
                        {totalObjects} objetos
                    </span>
                </div>
                <button
                    onClick={handleCopyAll}
                    className="flex items-center gap-2 text-[10px] font-black text-white/40 hover:text-white uppercase tracking-widest transition-all"
                >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    Copiar todo
                </button>
            </div>

            <div className="flex" style={{ minHeight: "520px" }}>
                {/* Tree Panel */}
                <div className="w-64 shrink-0 border-r border-white/8 bg-black/30 p-2 overflow-y-auto">
                    <p className="text-[9px] font-black text-white/20 uppercase tracking-widest px-3 py-2">
                        Objetos ABAP
                    </p>
                    {tree.map(node => (
                        <TreeNode
                            key={node.id}
                            node={node}
                            selectedId={selectedNode?.id}
                            onSelect={setSelectedNode}
                        />
                    ))}
                </div>

                {/* Code Panel */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    {selectedNode ? (
                        <>
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 bg-black/20">
                                <button
                                    onClick={() => setSelectedNode(null)}
                                    className="text-[9px] font-black text-white/30 hover:text-white uppercase tracking-widest transition-all mr-2"
                                >
                                    ← Volver
                                </button>
                                <span className={selectedNode.color}>{selectedNode.icon}</span>
                                <span className="text-[11px] font-black text-white/60 flex-1">{selectedNode.label}</span>
                                <button
                                    onClick={handleCopy}
                                    className="flex items-center gap-1 text-[9px] font-black text-white/30 hover:text-white uppercase tracking-widest transition-all"
                                >
                                    {copied ? <Check size={11} /> : <Copy size={11} />}
                                    Copiar
                                </button>
                            </div>
                            <div className="flex-1 overflow-auto">
                                <SyntaxHighlighter
                                    language={selectedNode.lang || "abap"}
                                    style={vscDarkPlus}
                                    customStyle={{
                                        padding: "1.5rem",
                                        fontSize: "12px",
                                        margin: 0,
                                        background: "#000000",
                                        minHeight: "100%",
                                    }}
                                    wrapLines={true}
                                    wrapLongLines={true}
                                >
                                    {selectedNode.code}
                                </SyntaxHighlighter>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center flex-col gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                                <Code2 size={28} className="text-blue-400/50" />
                            </div>
                            <div className="text-center">
                                <p className="text-[12px] font-black text-white/30 uppercase tracking-widest mb-1">
                                    Seleccioná un archivo
                                </p>
                                <p className="text-[10px] text-white/15 uppercase tracking-widest">
                                    para ver su código
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 justify-center mt-2 px-8">
                                {tree.flatMap(folder => folder.children).map(node => (
                                    <button
                                        key={node.id}
                                        onClick={() => setSelectedNode(node)}
                                        className={`text-[9px] font-black px-2 py-1 rounded-lg border transition-all uppercase tracking-wide ${node.color} border-white/10 hover:border-white/30 bg-white/3 hover:bg-white/8`}
                                    >
                                        {node.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RapExplorer;